<div class="activity-feed-container">
  <div class="feed-content">
    <!-- Post Creation Section -->
    <div class="create-post-card">
      <div class="create-post-header">
        <div class="user-avatar">
          <div class="avatar-circle" *ngIf="!getCurrentUser()?.profileImageUrl">
            {{ (getCurrentUser()?.firstName?.charAt(0) || 'U') + (getCurrentUser()?.lastName?.charAt(0) || '') }}
          </div>
          <img 
            *ngIf="getCurrentUser()?.profileImageUrl"
            [src]="getCurrentUser()?.profileImageUrl || defaultAvatarUrl" 
            [alt]="getCurrentUser()?.firstName"
            (error)="onImageError($event)"
            class="avatar-img" />
        </div>
        <div class="post-input-container">
          <form [formGroup]="postForm" (ngSubmit)="onCreatePost()">
            <textarea
              formControlName="textContent"
              class="post-textarea"
              placeholder="What's on your mind?"
              rows="3"
              [maxlength]="5000"></textarea>
            
            <!-- Media Previews -->
            <div class="media-previews" *ngIf="mediaPreviews.length > 0">
              <div class="media-preview-item" *ngFor="let preview of mediaPreviews; let i = index">
                <button type="button" class="remove-media-btn" (click)="removeMedia(i)" title="Remove">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2"/>
                    <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </button>
                <img *ngIf="preview.type === 'image'" [src]="preview.url" alt="Preview" class="preview-image" />
                <video *ngIf="preview.type === 'video'" [src]="preview.url" controls class="preview-video"></video>
              </div>
            </div>

            <div class="post-actions">
              <div class="media-actions">
                <label class="media-btn" [class.active]="showMediaSelector">
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*,video/*"
                    (change)="onMediaSelected($event)"
                    (click)="toggleMediaSelector()" />
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <polyline points="21,15 16,10 5,21" stroke="currentColor" stroke-width="2"/>
                  </svg>
                  <span>Photo/Video</span>
                </label>
              </div>
              <button 
                type="submit" 
                class="post-btn"
                [disabled]="isCreatingPost || (!postForm.get('textContent')?.value?.trim() && mediaPreviews.length === 0)">
                <span *ngIf="!isCreatingPost">Post</span>
                <span *ngIf="isCreatingPost">Posting...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div class="error-message" *ngIf="error">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2"/>
        <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2"/>
      </svg>
      <span>{{ error }}</span>
    </div>

    <!-- Posts Feed -->
    <div class="posts-feed" *ngIf="!isLoading && posts.length > 0">
      <div class="post-card" *ngFor="let post of posts">
        <!-- Post Header -->
        <div class="post-header">
          <div class="post-author-info">
            <img 
              [src]="post.author.profileImageUrl || defaultAvatarUrl" 
              [alt]="getAuthorName(post.author)"
              (error)="onImageError($event)"
              loading="lazy"
              class="author-avatar" />
            <div class="author-details">
              <div class="author-name">{{ getAuthorName(post.author) }}</div>
              <div class="post-time">{{ formatTime(post.createdAt) }}</div>
            </div>
          </div>
          <button 
            *ngIf="post.author.id === getCurrentUser()?.id"
            class="delete-post-btn"
            (click)="onDeletePost(post.id)"
            title="Delete post">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>

        <!-- Post Content -->
        <div class="post-content">
          <div *ngIf="post.textContent">
            <ng-container *ngIf="getPostYouTubeContent(post.id) as youtubeContent">
              <p *ngIf="youtubeContent.text" class="post-text">{{ youtubeContent.text }}</p>
              
              <!-- YouTube Embeds -->
              <div class="youtube-embeds" *ngIf="youtubeContent.youtubeUrls.length > 0">
                <div class="youtube-embed-container" *ngFor="let youtube of youtubeContent.youtubeUrls">
                  <iframe
                    [src]="youtube.embedUrl"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    class="youtube-iframe">
                  </iframe>
                </div>
              </div>
            </ng-container>
          </div>
          
          <!-- Post Media -->
          <div class="post-media" *ngIf="post.media && post.media.length > 0">
            <div class="media-grid" [class.single]="post.media.length === 1">
              <div class="media-item" *ngFor="let media of post.media">
                <img 
                  *ngIf="media.mediaType === 'image' && media.mediaUrl" 
                  [src]="getMediaUrl(media)" 
                  [alt]="media.fileName || 'Post image'"
                  (error)="onImageError($event)"
                  class="post-media-image"
                  loading="lazy" />
                <video 
                  *ngIf="media.mediaType === 'video' && media.mediaUrl" 
                  [src]="getMediaUrl(media)" 
                  controls
                  loading="lazy"
                  preload="metadata"
                  class="post-media-video"></video>
              </div>
            </div>
          </div>
        </div>

        <!-- Post Stats -->
        <div class="post-stats" *ngIf="post.likeCount > 0 || post.commentCount > 0">
          <span *ngIf="post.likeCount > 0" class="stat-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z" 
                    [attr.fill]="post.isLikedByCurrentUser ? '#e0245e' : 'none'"
                    [attr.stroke]="post.isLikedByCurrentUser ? '#e0245e' : 'currentColor'"
                    stroke-width="2"/>
            </svg>
            {{ post.likeCount }}
          </span>
          <span *ngIf="post.commentCount > 0" class="stat-item">
            {{ post.commentCount }} {{ post.commentCount === 1 ? 'comment' : 'comments' }}
          </span>
        </div>

        <!-- Post Actions -->
        <div class="post-actions-bar">
          <button 
            class="action-btn"
            [class.liked]="post.isLikedByCurrentUser"
            (click)="onLikePost(post)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z" 
                  [attr.fill]="post.isLikedByCurrentUser ? '#e0245e' : 'none'"
                  [attr.stroke]="post.isLikedByCurrentUser ? '#e0245e' : 'currentColor'"
                  stroke-width="2"/>
            </svg>
            <span>Like</span>
          </button>
          <button class="action-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>Comment</span>
          </button>
        </div>

        <!-- Comments Section -->
        <div class="comments-section">
          <!-- Existing Comments -->
          <div class="comment-list" *ngIf="getVisibleComments(post) && getVisibleComments(post).length > 0">
            <div class="comment-item" *ngFor="let comment of getVisibleComments(post)">
              <img 
                [src]="comment.author.profileImageUrl || defaultAvatarUrl" 
                [alt]="getAuthorName(comment.author)"
                (error)="onImageError($event)"
                class="comment-avatar" />
              <div class="comment-content">
                <div class="comment-header">
                  <span class="comment-author">{{ getAuthorName(comment.author) }}</span>
                  <span class="comment-time">{{ formatTime(comment.createdAt) }}</span>
                </div>
                <p class="comment-text">{{ comment.content }}</p>
              </div>
            </div>
          </div>

          <!-- Load More Comments Button -->
          <div class="load-more-comments" *ngIf="hasMoreComments(post)">
            <button 
              class="load-more-comments-btn"
              (click)="loadMoreComments(post)"
              [disabled]="isCommentsLoading(post.id)">
              <span *ngIf="!isCommentsLoading(post.id)">Load more comments</span>
              <span *ngIf="isCommentsLoading(post.id)" class="loading-text">
                <svg class="spinner-small" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                    <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416;0 31.416" repeatCount="indefinite"/>
                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416;-31.416" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Loading...
              </span>
            </button>
          </div>

          <!-- Add Comment -->
          <div class="add-comment">
            <div class="avatar-circle" *ngIf="!getCurrentUser()?.profileImageUrl">
              {{ (getCurrentUser()?.firstName?.charAt(0) || 'U') + (getCurrentUser()?.lastName?.charAt(0) || '') }}
            </div>
            <img 
              *ngIf="getCurrentUser()?.profileImageUrl"
              [src]="getCurrentUser()?.profileImageUrl || defaultAvatarUrl" 
              [alt]="getCurrentUser()?.firstName"
              (error)="onImageError($event)"
              class="comment-avatar" />
            <div class="comment-input-container">
              <input 
                type="text" 
                [id]="'comment-input-' + post.id"
                class="comment-input"
                placeholder="Write a comment..."
                maxlength="2000"
                (keyup.enter)="onAddComment(post, $any($event.target).value)" />
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Trigger (for Intersection Observer) -->
      <div id="load-more-trigger" *ngIf="hasMore && !isLoadingMore" style="height: 1px; width: 100%;"></div>
      
      <!-- Loading More Indicator (shown when scrolling) -->
      <div class="loading-more" *ngIf="isLoadingMore && !isLoading">
        <div class="spinner-small"></div>
        <span>Loading more posts...</span>
      </div>

      <!-- End of Feed Message -->
      <div class="end-of-feed" *ngIf="!hasMore && posts.length > 0 && !isLoadingMore">
        <p>You've reached the end of the feed</p>
      </div>
    </div>

    <!-- Loading State (Initial Load) -->
    <div class="loading-state" *ngIf="isLoadingInitial">
      <div class="spinner"></div>
      <span>Loading posts...</span>
    </div>

    <!-- Empty State -->
    <div class="empty-state" *ngIf="!isLoading && posts.length === 0">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2"/>
        <polyline points="14,2 14,8 20,8" stroke="currentColor" stroke-width="2"/>
        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="2"/>
        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="2"/>
        <polyline points="10,9 9,9 8,9" stroke="currentColor" stroke-width="2"/>
      </svg>
      <h3>No posts yet</h3>
      <p>Be the first to share something with your family!</p>
    </div>
  </div>
</div>

